import { useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import {
    DndContext,
    MouseSensor,
    TouchSensor,
    useDndMonitor,
    useDraggable,
    useSensor,
    useSensors,
    type Announcements,
    type ScreenReaderInstructions,
} from '@dnd-kit/core';
import { useUser } from '@/hooks/user/useUser';
import { SheetVisibilityContext } from '@/contexts/SheetVisibilityContext/SheetVisibilityContext';
import { useFloatingActionBarPosition } from '@/components/FloatingActionBar/useFloatingActionBarPosition';
import { useHasCoarsePointer } from '@/components/FloatingActionBar/useHasCoarsePointer';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import {
    DOCK_DRAG_HINT_ID,
    DOCK_GRIP_CLASS,
    DOCK_HOLD_MS,
    DOCK_HOLD_TOLERANCE_PX,
    DRAG_THRESHOLD_PX,
    isWithinDockGrip,
} from '@/components/FloatingActionBar/floatingActionBarPosition';
import { cn } from '@/lib/utils';

const DOCK_ANCHOR_CLASS = 'fixed right-4 bottom-[calc(3rem+env(safe-area-inset-bottom))]';

// The top radii track the pill's width, or the desktop top stops being round.
const DOCK_SHAPE_CLASS = 'rounded-[27px_27px_0px_38px] lg:rounded-[37px_37px_0px_52px]';

const DOCK_GHOST_SHAPE_CLASS = 'rounded-[27px] lg:rounded-[37px]';

// Keep in step with the pill's bottom padding classes below.
const GHOST_GRIP_BAND_PX = 12;

const DOCK_DRAGGABLE_ID = 'floating-action-bar';

const DOCK_LIFT_SCALE = 1.05;
// Driven over the hold's own duration, so the shrink is the only sign the press has registered.
const DOCK_PRESS_SCALE = 0.94;

// The bar announces its own position, which says more than "picked up draggable item" would.
const SILENT_ANNOUNCEMENTS: Announcements = {
    onDragStart: () => undefined,
    onDragOver: () => undefined,
    onDragEnd: () => undefined,
    onDragCancel: () => undefined,
};

// The keyboard path is the arrow-key nudge, not dnd-kit's space-bar pickup.
const NO_SCREEN_READER_INSTRUCTIONS: ScreenReaderInstructions = { draggable: '' };

export interface DragGripProps {
    /** Enlarges the hit area below the pill, for a finger. */
    hasOverhang: boolean;
}

// Dots and hit area are separate elements so the band can be finger-sized without scaling the
// visual. `touch-none` sits on the band alone, the one part that drags without a hold.
const DragGrip = ({ hasOverhang }: DragGripProps) => (
    <>
        <span
            aria-hidden
            className={cn(
                // Never the hit target. That is the band below, which must win where they overlap.
                'pointer-events-none absolute grid grid-cols-2 gap-[2px]',
                'bottom-1.5 right-1.5 lg:bottom-2 lg:right-2',
            )}
        >
            {Array.from({ length: 6 }, (_, i) => (
                <span
                    key={i}
                    className="size-[2px] rounded-full bg-gz-neutral-400 dark:bg-gz-marine-900"
                />
            ))}
        </span>
        <span
            aria-hidden
            data-testid="floating-action-bar-drag-handle"
            className={cn(
                DOCK_GRIP_CLASS,
                'absolute inset-x-0 touch-none',
                // The pill's own padding is under the WCAG 2.5.8 minimum, so for a finger the band
                // hangs below, stopping level with the lower button so it cannot steal a tap.
                hasOverhang ? '-bottom-4 h-8' : 'bottom-0 h-4 lg:h-[18px]',
            )}
        />
    </>
);

interface DockPillProps {
    userId: string;
    children?: ReactNode;
}

const DockPill = ({ userId, children }: DockPillProps) => {
    // Read the context directly. `useSheetVisibility()` throws outside its provider.
    const sheetVisibility = useContext(SheetVisibilityContext);
    const isSheetOpen = sheetVisibility?.isSheetOpen ?? false;

    const hasCoarsePointer = useHasCoarsePointer();
    const prefersReducedMotion = usePrefersReducedMotion();
    const nodeRef = useRef<HTMLDivElement>(null);
    const {
        position,
        size,
        announcement,
        clampOffset,
        onDragStart,
        onDragEnd,
        onKeyDown,
    } = useFloatingActionBarPosition(userId, nodeRef);

    const [isPending, setIsPending] = useState(false);
    const endPending = useCallback(() => setIsPending(false), []);

    // Only a delay constraint is a hold, so a mouse press never pends. A cancel is left
    // uncommitted, which is what returns the bar to where it was picked up.
    useDndMonitor({
        onDragPending: ({ constraint }) => setIsPending('delay' in constraint),
        onDragAbort: endPending,
        onDragStart: () => {
            endPending();
            onDragStart();
        },
        onDragEnd: ({ delta }) => {
            endPending();
            onDragEnd(delta);
        },
        onDragCancel: endPending,
    });

    // `attributes` is dropped on purpose. It would put `role="button"` and a tabindex on a group
    // and bind a space-bar pickup the arrow-key nudge already covers.
    const { isDragging, listeners, setNodeRef, transform } = useDraggable({ id: DOCK_DRAGGABLE_ID });

    // Clamped live, or the bar would follow the finger off-screen and snap back on release.
    const offset = clampOffset({
        x: position.x + (transform?.x ?? 0),
        y: position.y + (transform?.y ?? 0),
    });
    // Inline, since a `scale-*` utility lands on a different property than `transform`.
    const scale = isDragging ? DOCK_LIFT_SCALE : (isPending ? DOCK_PRESS_SCALE : 1);
    const dragTransform = `translate(${offset.x}px, ${offset.y}px)`
        + (scale === 1 ? '' : ` scale(${scale})`);
    // Only while pending. Under a finger the bar has to track it frame for frame.
    const transition = isPending && !prefersReducedMotion
        ? `transform ${DOCK_HOLD_MS}ms ease-out`
        : undefined;

    return (
        <>
            {/* Outside the pill so the ghost stays put while the bar moves. */}
            {isDragging && !isSheetOpen && (
                <div
                    aria-hidden
                    data-testid="floating-action-bar-home-ghost"
                    // marginBottom lifts it off the shared anchor, the height cut keeps tops level.
                    style={{
                        width: size.width,
                        height: size.height - GHOST_GRIP_BAND_PX,
                        marginBottom: GHOST_GRIP_BAND_PX,
                    }}
                    className={cn(
                        DOCK_ANCHOR_CLASS,
                        DOCK_GHOST_SHAPE_CLASS,
                        'z-[69] border-2 border-dashed',
                        'light:border-gz-marine-300 dark:border-white/30',
                    )}
                />
            )}
            <div
                ref={(node) => {
                    nodeRef.current = node;
                    setNodeRef(node);
                }}
                data-testid="floating-action-bar"
                style={{ transform: dragTransform, transition }}
                onKeyDown={onKeyDown}
                // The Hub glyph is an <img>. A native drag would paint a ghost and cut off the
                // mousemoves this drag runs on.
                onDragStart={event => event.preventDefault()}
                {...listeners}
                // Announced as one thing rather than two buttons adrift at the end of the page.
                role="group"
                aria-label="Quick actions"
                className={cn(
                    // Safe-area in the offset, not the padding, so the pill stays symmetric.
                    DOCK_ANCHOR_CLASS,
                    DOCK_SHAPE_CLASS,
                    // No `relative` here. It would displace `fixed`, which anchors the grip.
                    'z-[70] flex flex-col items-center',
                    'gap-2 p-1 pb-4 lg:gap-3 lg:p-1.5 lg:pb-[18px]',
                    'bg-gz-marine-200 dark:bg-gz-marine-600',
                    isDragging ? 'shadow-2xl' : 'shadow-lg',
                    // Keyed on holding an action, not `:empty`, which the grip would defeat.
                    '[&:not(:has(button))]:hidden',
                    // Hidden rather than unmounted. Radix makes outside content inert, and
                    // unmounting would destroy in-flight child state like the ZigBot age gate.
                    isSheetOpen && 'hidden',
                    'cursor-grab active:cursor-grabbing select-none',
                )}
            >
                {children}
                <DragGrip hasOverhang={hasCoarsePointer} />
            </div>
            {/* Outside the pill so it is not carried around. `aria-describedby` resolves by id. */}
            <span id={DOCK_DRAG_HINT_ID} className="sr-only">
                Use the arrow keys to move this bar. Press Home to return it to its default position.
            </span>
            <span className="sr-only" aria-live="polite">{announcement}</span>
        </>
    );
};

export interface FloatingActionBarProps {
    children?: ReactNode;
}

// Takes `children` rather than a list of items so each action owns its visibility rules.
export const FloatingActionBar = ({ children }: FloatingActionBarProps) => {
    const { user } = useUser();

    // A finger press could still turn out to be a tap or a scroll, so it waits out a hold and
    // gives up the moment it moves. The grip skips the wait.
    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: { distance: DRAG_THRESHOLD_PX },
    });
    const touchSensor = useSensor(TouchSensor, {
        activationConstraint: { delay: DOCK_HOLD_MS, tolerance: DOCK_HOLD_TOLERANCE_PX },
        bypassActivationConstraint: ({ event }) => isWithinDockGrip(event.target),
        // Android only. iOS has no Vibration API, so the lift carries this on its own there.
        onActivation: () => navigator.vibrate?.(10),
    });
    const sensors = useSensors(mouseSensor, touchSensor);

    if (!user) {
        return null;
    }

    return (
        <DndContext
            sensors={sensors}
            accessibility={{
                announcements: SILENT_ANNOUNCEMENTS,
                screenReaderInstructions: NO_SCREEN_READER_INSTRUCTIONS,
            }}
        >
            <DockPill userId={user.id}>{children}</DockPill>
        </DndContext>
    );
};
